/**
 * Fixed-timestep driver + score maths. Pure & framework-agnostic so behaviour
 * is identical across 60/90/120 Hz displays and is unit-testable.
 */
import { SIM, SCORE } from './constants';

/**
 * Accumulator-based fixed-timestep clock. Feed it the render frame delta; it
 * returns how many fixed steps of {@link SIM.fixedDeltaMs} to run this frame and
 * carries the remainder. The incoming delta is clamped to {@link SIM.maxFrameMs}
 * to avoid the spiral-of-death and time jumps after the tab is backgrounded.
 */
export class FixedStepClock {
  private acc = 0;

  /** Returns the number of fixed steps to execute for this render frame. */
  step(frameMs: number): number {
    this.acc += Math.min(frameMs, SIM.maxFrameMs);
    let steps = 0;
    while (this.acc >= SIM.fixedDeltaMs) {
      this.acc -= SIM.fixedDeltaMs;
      steps++;
    }
    return steps;
  }

  /** Drop any accumulated time (e.g. after a pause/resume). */
  reset(): void {
    this.acc = 0;
  }
}

/** Fixed step length in seconds — convenient for px/s velocity maths. */
export const fixedDeltaSeconds = SIM.fixedDeltaMs / 1000;

/** Survival score derived from elapsed alive time (ms). */
export function scoreFromElapsedMs(elapsedMs: number): number {
  return Math.floor((elapsedMs / 1000) * SCORE.perSecond);
}
